import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Download, X, Share } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { isGuestShellPath } from "@/lib/guestNav";
import { useGuestBrand } from "@/lib/guestBrand";

/**
 * Home-screen invitation, offered at the moment it earns its keep.
 *
 * Deliberately NOT on arrival: a guest who just scanned a room QR because
 * they feel unwell should not be asked to install anything. We wait until
 *   1. they are signed in and inside the guest portal,
 *   2. they have care in flight (an episode or something needing them), and
 *   3. they are either back for a second session or have settled in (90s).
 *
 * Chrome/Edge get the real one-tap install via `beforeinstallprompt`; iOS has
 * no install API, so it gets the Share → Add to Home Screen steps. Dismissal
 * snoozes for 30 days; a completed install silences it for good.
 */
const KEY = "aura-install-state";
const LEGACY_DISMISS_KEY = "aura-install-dismissed-at";
const SNOOZE_DAYS = 30;
const DWELL_MS = 90_000;

function readState() {
  try {
    const s = JSON.parse(localStorage.getItem(KEY) || "{}");
    // Honour dismissals recorded by the previous version of this prompt.
    const legacy = Number(localStorage.getItem(LEGACY_DISMISS_KEY));
    if (legacy && !s.snooze_until) s.snooze_until = legacy + SNOOZE_DAYS * 86_400_000;
    return s;
  } catch { return {}; }
}

function writeState(patch) {
  const next = { ...readState(), ...patch };
  try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* private mode */ }
  return next;
}

function isStandalone() {
  return window.matchMedia?.("(display-mode: standalone)").matches
    || window.navigator.standalone === true;
}

function isIos() {
  const ua = window.navigator.userAgent;
  return (/iPad|iPhone|iPod/.test(ua) && !window.MSStream)
    || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

/** Counts this browser session once, so "they came back" is measurable. */
function noteSession() {
  const state = readState();
  if (sessionStorage.getItem("aura-install-session")) return state;
  sessionStorage.setItem("aura-install-session", "1");
  return writeState({
    sessions: (state.sessions || 0) + 1,
    first_seen: state.first_seen || Date.now(),
  });
}

export function InstallPrompt() {
  const { user } = useAuth();
  const location = useLocation();
  const { brandName } = useGuestBrand();
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  const eligibleSurface = user?.role === "patient" && isGuestShellPath(location.pathname);

  useEffect(() => {
    if (!eligibleSurface || isStandalone()) return;

    const state = noteSession();
    if (state.installed) return;
    if (state.snooze_until && Date.now() < state.snooze_until) return;

    let cancelled = false;
    let prompt = null;
    const timers = [];

    const onBeforeInstall = (e) => {
      e.preventDefault();
      prompt = e;
      setDeferred(e);
      maybeShow();
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    const settled = () => (state.sessions || 0) >= 2
      || Date.now() - (state.first_seen || Date.now()) >= DWELL_MS;

    const maybeShow = async (attempt = 0) => {
      if (cancelled || !settled()) return;
      // Never stack on top of the privacy banner — two sheets fighting for the
      // same corner is worse than waiting for one of them to go.
      if (document.querySelector('[data-testid="privacy-banner"]')) {
        if (attempt < 15) {
          const t = setTimeout(() => maybeShow(attempt + 1), 4000);
          timers.push(t);
        }
        return;
      }
      const ios = isIos();
      if (!ios && !prompt) return;   // nothing to offer yet
      // Only worth asking once they actually have care to come back to.
      try {
        const { data } = await api.get("/guest/now");
        const hasCare = (data?.episode_count || 0) > 0 || (data?.actions || []).length > 0;
        if (!hasCare || cancelled) return;
      } catch { return; }
      setIosHint(ios);
      setVisible(true);
    };

    if (settled()) maybeShow();
    else timers.push(setTimeout(() => maybeShow(),
      DWELL_MS - (Date.now() - (state.first_seen || Date.now())) + 500));

    return () => {
      cancelled = true;
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      timers.forEach(clearTimeout);
    };
  }, [eligibleSurface]);

  const dismiss = () => {
    writeState({ snooze_until: Date.now() + SNOOZE_DAYS * 86_400_000 });
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      writeState(outcome === "accepted"
        ? { installed: true }
        : { snooze_until: Date.now() + SNOOZE_DAYS * 86_400_000 });
    } catch { /* silent */ }
    setDeferred(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed left-4 right-4 bottom-4 z-30 mb-20 lg:mb-0 sm:left-auto sm:right-4 sm:max-w-sm animate-in fade-in slide-in-from-bottom-4 duration-300"
      role="dialog"
      aria-label="Add AllRound Concierge to your home screen"
      data-testid="pwa-install-prompt"
    >
      <div className="rounded-2xl p-4 flex items-start gap-3 shadow-xl"
           style={{ background: "#201D17", color: "#F7F1E8" }}>
        <span className="h-10 w-10 rounded-2xl flex items-center justify-center shrink-0"
              style={{ background: "rgba(198,161,91,0.22)" }}>
          <Download className="h-4 w-4" style={{ color: "#C6A15B" }} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.24em] font-bold" style={{ color: "#C6A15B" }}>
            Keep us one tap away
          </div>
          <div className="text-sm mt-1.5 leading-snug opacity-85">
            {iosHint
              ? <>Add {brandName ? `${brandName}'s concierge` : "the concierge"} to your
                  home screen: tap <Share className="h-3.5 w-3.5 inline -mt-0.5" /> Share,
                  then <b>Add to Home Screen</b>.</>
              : <>Install {brandName ? `${brandName}'s concierge` : "the concierge"} so
                  results, replies and your care timeline open instantly.</>}
          </div>
          {!iosHint && deferred && (
            <button
              onClick={install}
              className="mt-3 inline-flex items-center gap-2 h-10 px-5 rounded-full text-sm font-semibold"
              style={{ background: "#C6A15B", color: "#201D17" }}
              data-testid="pwa-install-btn"
            >
              <Download className="h-3.5 w-3.5" /> Add to home screen
            </button>
          )}
        </div>
        <button
          onClick={dismiss}
          className="shrink-0 opacity-60 hover:opacity-100 transition-opacity"
          aria-label="Not now"
          data-testid="pwa-install-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default InstallPrompt;
